import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  Badge,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Banner,
  useIndexResourceState,
  InlineStack,
  Box,
  BlockStack,
  type IndexFiltersProps,
  type TabProps,
} from '@shopify/polaris';
import {
  contactService,
  type Contact,
  type ContactStatus,
} from '../../services/content.service';

const STATUS_OPTIONS: { label: string; value: ContactStatus }[] = [
  { label: 'New', value: 'NEW' },
  { label: 'Read', value: 'READ' },
  { label: 'Responded', value: 'RESPONDED' },
  { label: 'Resolved', value: 'RESOLVED' },
];

function getStatusBadge(status: ContactStatus) {
  const toneMap: Record<ContactStatus, 'warning' | 'info' | 'success' | 'attention' | undefined> = {
    NEW: 'warning',
    READ: 'info',
    RESPONDED: 'attention',
    RESOLVED: 'success',
  };

  return <Badge tone={toneMap[status]}>{status}</Badge>;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ContactListPage() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ContactStatus | ''>('');
  const [page, setPage] = useState(1);

  // View/Reply Modal
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const { mode, setMode } = useSetIndexFiltersMode();
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(contacts as unknown as {[key: string]: unknown}[]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await contactService.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setContacts(result.data);
      setTotal(result.meta.total);
    } catch (err) {
      setError('Failed to load contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusFilterChange = useCallback((value: string[]) => {
    setStatusFilter((value[0] as ContactStatus) || '');
    setPage(1);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    setStatusFilter('');
    setPage(1);
  }, []);

  const filters: IndexFiltersProps['filters'] = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange([e.target.value])}
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
  if (statusFilter) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
      onRemove: () => setStatusFilter(''),
    });
  }

  const tabs: TabProps[] = [
    { id: 'all', content: 'All', accessibilityLabel: 'All contacts' },
    { id: 'new', content: 'New', accessibilityLabel: 'New contacts' },
    { id: 'responded', content: 'Responded', accessibilityLabel: 'Responded contacts' },
    { id: 'resolved', content: 'Resolved', accessibilityLabel: 'Resolved contacts' },
  ];

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = useCallback((tabIndex: number) => {
    setSelectedTab(tabIndex);
    const tabStatuses: Record<number, ContactStatus | ''> = {
      0: '',
      1: 'NEW',
      2: 'RESPONDED',
      3: 'RESOLVED',
    };
    setStatusFilter(tabStatuses[tabIndex] || '');
    setPage(1);
  }, []);

  const openContactModal = async (contact: Contact) => {
    setSelectedContact(contact);
    setReplyMessage('');
    setShowModal(true);

    // Mark as read if it's new
    if (contact.status === 'NEW') {
      try {
        await contactService.updateStatus(contact.id, 'READ');
        await loadData();
      } catch (err) {
        console.error('Failed to mark as read', err);
      }
    }
  };

  const handleSendReply = async () => {
    if (!selectedContact || !replyMessage.trim()) return;

    try {
      setSendingReply(true);
      await contactService.reply(selectedContact.id, replyMessage.trim());
      setShowModal(false);
      setReplyMessage('');
      await loadData();
    } catch (err) {
      setError('Failed to send reply');
      console.error(err);
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (contactId: string, newStatus: ContactStatus) => {
    try {
      await contactService.updateStatus(contactId, newStatus);
      await loadData();
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    }
  };

  const rowMarkup = contacts.map((contact, index) => (
    <IndexTable.Row
      id={contact.id}
      key={contact.id}
      selected={selectedResources.includes(contact.id)}
      position={index}
      onClick={() => openContactModal(contact)}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {contact.name}
        </Text>
        <Text variant="bodySm" tone="subdued" as="p">
          {contact.email}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold" as="span">
          {contact.subject}
        </Text>
        <Text variant="bodySm" tone="subdued" as="p">
          {contact.message.slice(0, 60)}
          {contact.message.length > 60 ? '...' : ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {contact.phone || (
          <Text variant="bodySm" tone="subdued" as="span">
            -
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>{getStatusBadge(contact.status)}</IndexTable.Cell>
      <IndexTable.Cell>{formatDate(contact.createdAt)}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => openContactModal(contact)}>
            View
          </Button>
          {contact.status !== 'RESOLVED' && (
            <Button
              size="slim"
              onClick={() => {
                handleUpdateStatus(contact.id, 'RESOLVED');
              }}
            >
              Resolve
            </Button>
          )}
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Contact Messages" fullWidth>
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Box>
      )}

      <Card padding="0">
        <IndexFilters
          queryValue=""
          queryPlaceholder=""
          onQueryChange={() => {}}
          onQueryClear={() => {}}
          tabs={tabs}
          selected={selectedTab}
          onSelect={handleTabChange}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={handleFiltersClearAll}
          mode={mode}
          setMode={setMode}
          canCreateNewView={false}
          hideQueryField
        />
        <IndexTable
          resourceName={{ singular: 'contact', plural: 'contacts' }}
          itemCount={contacts.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          headings={[
            { title: 'From' },
            { title: 'Message' },
            { title: 'Phone' },
            { title: 'Status' },
            { title: 'Received' },
            { title: 'Actions' },
          ]}
          loading={loading}
          pagination={{
            hasNext: page * 20 < total,
            hasPrevious: page > 1,
            onNext: () => setPage((p) => p + 1),
            onPrevious: () => setPage((p) => p - 1),
          }}
        >
          {rowMarkup}
        </IndexTable>
      </Card>

      {/* View/Reply Modal */}
      {selectedContact && (
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Contact Message"
          primaryAction={
            selectedContact.status !== 'RESOLVED'
              ? {
                  content: 'Send Reply',
                  loading: sendingReply,
                  disabled: !replyMessage.trim(),
                  onAction: handleSendReply,
                }
              : undefined
          }
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setShowModal(false),
            },
          ]}
          size="large"
        >
          <Modal.Section>
            <BlockStack gap="400">
              {/* Contact Details */}
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h3" variant="headingMd">
                      {selectedContact.subject}
                    </Text>
                    {getStatusBadge(selectedContact.status)}
                  </InlineStack>

                  <InlineStack gap="400">
                    <BlockStack gap="100">
                      <Text as="p" tone="subdued" variant="bodySm">
                        From
                      </Text>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {selectedContact.name}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="p" tone="subdued" variant="bodySm">
                        Email
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {selectedContact.email}
                      </Text>
                    </BlockStack>
                    {selectedContact.phone && (
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued" variant="bodySm">
                          Phone
                        </Text>
                        <Text as="p" variant="bodyMd">
                          {selectedContact.phone}
                        </Text>
                      </BlockStack>
                    )}
                    <BlockStack gap="100">
                      <Text as="p" tone="subdued" variant="bodySm">
                        Received
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {formatDate(selectedContact.createdAt)}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>

              {/* Message */}
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">
                    Message
                  </Text>
                  <Box
                    padding="300"
                    background="bg-surface-secondary"
                    borderRadius="200"
                  >
                    <Text as="p" variant="bodyMd">
                      {selectedContact.message}
                    </Text>
                  </Box>
                </BlockStack>
              </Card>

              {/* Reply Form */}
              {selectedContact.status !== 'RESOLVED' && (
                <Card>
                  <FormLayout>
                    <TextField
                      label="Reply Message"
                      value={replyMessage}
                      onChange={setReplyMessage}
                      multiline={4}
                      autoComplete="off"
                      helpText="This will be sent to the customer's email"
                    />
                  </FormLayout>
                </Card>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
